/* 문항 그림의 시점 복원. 그림에서 읽은 꼭짓점의 화면 위치와 장면의 3D 좌표로 그 그림과 같은 시점의 카메라 방향을 구한다 */
import type { V3 } from './independent3d';

/** 문항 그림 위의 이름 붙은 점. x, y 는 그림 안에서의 위치이고 단위는 임의이다(y 는 아래로 커진다) */
export interface FigurePoint { name: string; x: number; y: number }

export interface ViewFit {
  /** 화면의 오른쪽, 위쪽, 화면에서 보는 사람 쪽을 가리키는 세계 좌표의 단위 벡터 */
  right: V3;
  up: V3;
  toward: V3;
  /** 그림 크기(점들이 펼쳐진 가장 긴 변)로 나눈 재현 오차의 제곱평균제곱근 */
  rms: number;
  points: number;
}

const dot = (a: number[], b: number[]): number => a.reduce((s, v, i) => s + v * b[i], 0);
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/** 3x3 대칭 행렬의 역행렬(여인수). 특이하면 null */
function inverse3(m: number[][]): number[][] | null {
  const [[a, b, c], [d, e, f], [g, h, i]] = m;
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-9) return null;
  return [
    [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
    [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
    [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det],
  ];
}

/** 세계 좌표 world 와 그림 위치 figure 에서 시점을 구한다. 이름이 겹치는 점이 4개 미만이거나 한 평면 위에 있으면 null */
export function fitViewFromFigure(world: Record<string, V3>, figure: FigurePoint[]): ViewFit | null {
  const pairs = figure.filter((p) => world[p.name] && Number.isFinite(p.x) && Number.isFinite(p.y)).map((p) => ({ x: world[p.name], u: [p.x, -p.y] }));
  if (pairs.length < 4) return null;
  const n = pairs.length;
  const cx = [0, 1, 2].map((k) => pairs.reduce((s, p) => s + p.x[k], 0) / n);
  const cu = [0, 1].map((k) => pairs.reduce((s, p) => s + p.u[k], 0) / n);
  const xs = pairs.map((p) => p.x.map((v, k) => v - cx[k]));
  const us = pairs.map((p) => p.u.map((v, k) => v - cu[k]));
  const span = Math.max(...[0, 1].map((k) => Math.max(...pairs.map((p) => p.u[k])) - Math.min(...pairs.map((p) => p.u[k]))));
  if (!(span > 0)) return null;
  // 아핀 카메라 u = M x 의 최소제곱: M = (sum u x^T)(sum x x^T)^-1
  const xx = [0, 1, 2].map((r) => [0, 1, 2].map((c) => xs.reduce((s, p) => s + p[r] * p[c], 0)));
  const inv = inverse3(xx);
  if (!inv) return null;
  const ux = [0, 1].map((r) => [0, 1, 2].map((c) => us.reduce((s, p, i) => s + p[r] * xs[i][c], 0)));
  const m = ux.map((row) => [0, 1, 2].map((c) => dot(row, inv.map((line) => line[c]))));
  // 가장 가까운 정규직교 두 행: (m m^T)^(-1/2) m
  const g = [[dot(m[0], m[0]), dot(m[0], m[1])], [dot(m[1], m[0]), dot(m[1], m[1])]];
  const tr = g[0][0] + g[1][1];
  const det = g[0][0] * g[1][1] - g[0][1] * g[1][0];
  const s = Math.sqrt(Math.max(det, 0));
  const t = Math.sqrt(Math.max(tr + 2 * s, 1e-12));
  // 2x2 행렬 제곱근 (g + sI)/t, 그 역행렬로 행을 정규직교화한다
  const root = [[(g[0][0] + s) / t, g[0][1] / t], [g[1][0] / t, (g[1][1] + s) / t]];
  const rd = root[0][0] * root[1][1] - root[0][1] * root[1][0];
  if (Math.abs(rd) < 1e-12) return null;
  const ri = [[root[1][1] / rd, -root[0][1] / rd], [-root[1][0] / rd, root[0][0] / rd]];
  const q = [0, 1].map((r) => [0, 1, 2].map((c) => ri[r][0] * m[0][c] + ri[r][1] * m[1][c]));
  const right = q[0] as V3; const up = q[1] as V3;
  const toward = cross(right, up);
  // 회전만으로 맞출 때의 재현 오차. 배율은 최소제곱으로 구한다
  const proj = xs.map((p) => [dot(right, p), dot(up, p)]);
  const scale = proj.reduce((sum, p, i) => sum + dot(p, us[i]), 0) / (proj.reduce((sum, p) => sum + dot(p, p), 0) || 1);
  const sq = proj.reduce((sum, p, i) => sum + (scale * p[0] - us[i][0]) ** 2 + (scale * p[1] - us[i][1]) ** 2, 0);
  return { right, up, toward, rms: Math.sqrt(sq / n) / span, points: n };
}

/** 방향 하나(보는 사람 쪽)를 정하면 그 방향에서 본 투영의 오른쪽과 위쪽. 위쪽 기준은 세계의 z 축이다 */
export function viewFromDirection(toward: V3): { right: V3; up: V3 } {
  const len = Math.hypot(...toward) || 1;
  const z: V3 = [toward[0] / len, toward[1] / len, toward[2] / len];
  const axis: V3 = Math.abs(z[2]) > 0.999 ? [0, 1, 0] : [0, 0, 1];
  const r = cross(axis, z); const rl = Math.hypot(...r) || 1;
  const right: V3 = [r[0] / rl, r[1] / rl, r[2] / rl];
  return { right, up: cross(z, right) };
}

/** 주어진 시점으로 world 를 투영해 같은 방식으로 재현 오차를 잰다. 기본 시점과 비교하는 데 쓴다 */
export function reprojectionError(world: Record<string, V3>, figure: FigurePoint[], right: V3, up: V3): number {
  const pairs = figure.filter((p) => world[p.name]).map((p) => ({ x: world[p.name], u: [p.x, -p.y] }));
  if (pairs.length < 3) return Number.NaN;
  const n = pairs.length;
  const cx = [0, 1, 2].map((k) => pairs.reduce((s, p) => s + p.x[k], 0) / n);
  const cu = [0, 1].map((k) => pairs.reduce((s, p) => s + p.u[k], 0) / n);
  const xs = pairs.map((p) => p.x.map((v, k) => v - cx[k]));
  const us = pairs.map((p) => p.u.map((v, k) => v - cu[k]));
  const span = Math.max(...[0, 1].map((k) => Math.max(...pairs.map((p) => p.u[k])) - Math.min(...pairs.map((p) => p.u[k]))));
  const proj = xs.map((p) => [dot(right, p), dot(up, p)]);
  const scale = proj.reduce((sum, p, i) => sum + dot(p, us[i]), 0) / (proj.reduce((sum, p) => sum + dot(p, p), 0) || 1);
  const sq = proj.reduce((sum, p, i) => sum + (scale * p[0] - us[i][0]) ** 2 + (scale * p[1] - us[i][1]) ** 2, 0);
  return Math.sqrt(sq / n) / span;
}
